import React, {
	useState,
	useEffect,
	useRef,
	useCallback,
	Fragment,
} from "react";
import {
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Paper,
	Switch,
	Tooltip,
	Card,
	CardContent,
	Divider,
	Stack,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";
import { useVehicleData } from "../../DataContext";

dayjs.extend(utc);
dayjs.extend(relativeTime);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_WS_URL = process.env.REACT_APP_BACKEND_WS_URL;
const BACKEND_TOKEN = process.env.REACT_APP_BACKEND_TOKEN;

// color helpers
const hashHue = (s) =>
	(Array.from(String(s)).reduce((a, c) => a + c.charCodeAt(0), 0) % 12) * 30; // 0..330
const groupAccent = (title) => `hsl(${hashHue(title || "g")}, 70%, 45%)`;

export default function CANData({ deviceId, chassisNum }) {
	const theme = useTheme();
	const isXs = useMediaQuery(theme.breakpoints.down("sm"));
	const compact = isXs;

	const [message, setMessage] = useState({
		timestamp: "",
		header: [],
		can_data: [],
	});
	const [payloadTime, setPayloadTime] = useState(null);
	const [lastSeen, setLastSeen] = useState("");
	const { setCanSnapshot } = useVehicleData();

	const wsRef = useRef(null);

	// Human-readable “last seen”
	const formatDiff = useCallback((time) => {
		const now = dayjs();
		const diffSec = now.diff(time, "second");
		if (diffSec < 60) return `${diffSec}s ago`;
		const diffMin = now.diff(time, "minute");
		if (diffMin < 60) return `${diffMin}m ${diffSec % 60}s ago`;
		const diffHr = now.diff(time, "hour");
		if (diffHr < 24) return `${diffHr}h ${diffMin % 60}m ago`;
		const diffDay = now.diff(time, "day");
		if (diffDay < 7) return `${diffDay}d ${diffHr % 24}h ago`;
		const diffWeek = Math.floor(diffDay / 7);
		return `${diffWeek}w ${diffDay % 7}d ago`;
	}, []);

	// Initial REST fetch + WebSocket subscribe
	useEffect(() => {
		if (!deviceId) return;

		const controller = new AbortController();
		fetch(`${BACKEND_URL}devices/${deviceId}/latest/`, {
			headers: {
				Accept: "application/json",
				Authorization: `Bearer ${BACKEND_TOKEN}`,
			},
			signal: controller.signal,
		})
			.then((res) => {
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				return res.json();
			})
			.then((data) => {
				const payload = data?.data ?? data;
				setMessage(payload);
				setCanSnapshot(payload);
				const ts = dayjs.utc(payload.timestamp).local();
				setPayloadTime(ts);
				setLastSeen(formatDiff(ts));
			})
			.catch((err) => console.error("Initial fetch error:", err));

		const ws = new WebSocket(
			`${BACKEND_WS_URL}ws/live/devices/${deviceId}`
		);
		wsRef.current = ws;
		ws.onmessage = (evt) => {
			try {
				const incoming = JSON.parse(evt.data);
				const payload = incoming?.data ?? incoming;
				setMessage(payload);
				setCanSnapshot(payload);
				const ts = dayjs.utc(payload.timestamp).local();
				setPayloadTime(ts);
				setLastSeen(formatDiff(ts));
			} catch (err) {
				console.error("WS parse error:", err);
			}
		};
		ws.onerror = (err) => console.error("WS error:", err);

		return () => {
			controller.abort();
			ws.close();
		};
	}, [deviceId, formatDiff, setCanSnapshot]);

	// Refresh “last seen” every second
	useEffect(() => {
		if (!payloadTime) return;
		const timer = setInterval(
			() => setLastSeen(formatDiff(payloadTime)),
			1000
		);
		return () => clearInterval(timer);
	}, [payloadTime, formatDiff]);

	// ---- rows table (shared) ----
	const renderRowsTable = (rows) => {
		return (
			<Table
				size="small"
				sx={{
					tableLayout: "fixed",
					"& tbody tr:nth-of-type(odd) td": compact
						? {}
						: { bgcolor: "action.hover" },
					"& th, & td": {
						borderColor: "divider",
						px: compact ? 0.5 : 1,
						py: compact ? 0.25 : 0.5,
					},
					"& td > *": { lineHeight: compact ? 1.1 : 1.3 },
				}}
			>
				<TableHead>
					<TableRow>
						<TableCell
							sx={{
								width: "42%",
								py: compact ? 0.25 : 0.5,
								fontWeight: 700,
								fontSize: compact ? 12 : 14,
							}}
						>
							Field
						</TableCell>
						<TableCell
							sx={{
								width: "58%",
								py: compact ? 0.25 : 0.5,
								fontWeight: 700,
								fontSize: compact ? 12 : 14,
							}}
						>
							Value
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{(rows || []).map((info) => {
						const range = info?.range;
						const isGraph =
							info?.is_graph &&
							Array.isArray(range) &&
							range.length === 2;

						let gauge = null;
						if (isGraph) {
							const [min, max] = range;
							const span = max - min || 1;
							const raw = Number(info.raw_value ?? 0);
							const pct = ((raw - min) / span) * 100;
							const zeroPos =
								min < 0 && max > 0 ? (-min / span) * 100 : 0;
							const clampedPct = Math.max(0, Math.min(100, pct));
							const positiveWidth =
								clampedPct >= zeroPos
									? clampedPct - zeroPos
									: 0;
							const negativeWidth =
								clampedPct < zeroPos ? zeroPos - clampedPct : 0;

							gauge = (
								<Box
									sx={{
										position: "relative",
										width: { xs: 84, sm: 110 },
										height: 8,
										bgcolor: "#eee",
										borderRadius: 4,
									}}
								>
									{negativeWidth > 0 && (
										<Box
											sx={{
												position: "absolute",
												left: `${
													zeroPos - negativeWidth
												}%`,
												width: `${negativeWidth}%`,
												height: "100%",
												bgcolor: info.color || "#888",
												borderRadius: "5px 0 0 5px",
											}}
										/>
									)}
									<Box
										sx={{
											position: "absolute",
											left: `${zeroPos}%`,
											width: `${positiveWidth}%`,
											height: "100%",
											bgcolor: info.color || "#888",
											borderRadius:
												min >= 0 ? 5 : "0 5px 5px 0",
										}}
									/>
								</Box>
							);
						}

						const valueCell =
							typeof info?.display_value === "boolean" ? (
								info?.is_alert ? (
									<Typography
										sx={{ fontWeight: 700 }}
										color={
											info.raw_value
												? "error.main"
												: "success.main"
										}
									>
										{info.raw_value ? "ACTIVE" : "OK"}
									</Typography>
								) : (
									<Switch
										checked={Boolean(info?.raw_value)}
										disabled
										size={isXs ? "small" : "medium"}
									/>
								)
							) : isGraph && gauge && !isXs ? (
								<Stack
									direction="row"
									spacing={1}
									alignItems="center"
								>
									{gauge}
									<Typography
										sx={{ fontWeight: 700 }}
										color={info?.color || "inherit"}
										variant="body2"
									>
										{String(info?.display_value)}
									</Typography>
								</Stack>
							) : (
								<Typography
									sx={{ fontWeight: 700 }}
									color={info?.color || "inherit"}
									variant="body2"
								>
									{String(info?.display_value ?? "—")}
								</Typography>
							);

						return (
							<TableRow
								key={info.key}
								sx={{ "&:last-child td": { border: 0 } }}
							>
								<TableCell sx={{ pr: compact ? 0.5 : 1 }}>
									{info?.label || info.key}
								</TableCell>
								<TableCell sx={{ verticalAlign: "middle" }}>
									<Tooltip title={info?.raw_hex || ""}>
										{valueCell}
									</Tooltip>
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		);
	};

	// ---- group renderers (CAN id & payload shown in groups, per spec) ----
	const renderRegular = (g) => {
		const { id, payload_hex } = g.can || {};
		return (
			<Paper
				variant="outlined"
				square={isXs} // add this
				sx={{
					p: compact ? 0.5 : 1.25,
					height: "100%",
					borderTop: `${compact ? 2 : 3}px solid ${groupAccent(
						g.title
					)}`,
				}}
			>
				<Stack spacing={compact ? 0.5 : 0.75}>
					<Typography
						variant={compact ? "caption" : "subtitle2"}
						fontWeight={800}
						sx={{
							color: groupAccent(g.title),
							display: "inline-block",
						}}
					>
						{g.title}
					</Typography>
					<Typography
						variant="caption"
						sx={{ fontFamily: "monospace" }}
					>
						CAN: {id || "—"} | Payload: {payload_hex || "—"}
					</Typography>
					<Divider />
					{renderRowsTable(g.rows || [])}
				</Stack>
			</Paper>
		);
	};

	const renderMerged = (g) => {
		return (
			<Paper
				variant="outlined"
				square={isXs} // add this
				sx={{
					p: compact ? 0.5 : 1.25,
					height: "100%",
					borderTop: `${compact ? 2 : 3}px solid ${groupAccent(
						g.title
					)}`,
				}}
			>
				<Stack spacing={compact ? 0.5 : 0.75}>
					<Typography
						variant={compact ? "caption" : "subtitle2"}
						fontWeight={800}
						sx={{
							color: groupAccent(g.title),
							display: "inline-block",
						}}
					>
						{g.title}
					</Typography>
					{(g.subgroups || []).map((sg, idx) => (
						<Fragment key={`${sg.title}-${idx}`}>
							{idx > 0 && <Divider />}
							<Stack spacing={0.5}>
								<Stack
									direction="row"
									spacing={compact ? 0.5 : 1}
									alignItems="baseline"
									justifyContent="space-between"
								>
									<Typography
										variant={compact ? "caption" : "body2"}
										fontWeight={700}
									>
										{sg.title}
									</Typography>
									<Typography
										variant="caption"
										sx={{ fontFamily: "monospace" }}
									>
										CAN: {sg.can?.id || "—"} | Payload:{" "}
										{sg.can?.payload_hex || "—"}
									</Typography>
								</Stack>
								{renderRowsTable(sg.rows || [])}
							</Stack>
						</Fragment>
					))}
				</Stack>
			</Paper>
		);
	};

	const renderSplit = (g) => {
		return (
			<Paper
				variant="outlined"
				square={isXs} // add this
				sx={{
					p: compact ? 0.5 : 1.25,
					height: "100%",
					borderTop: `${compact ? 2 : 3}px solid ${groupAccent(
						g.title
					)}`,
				}}
			>
				<Stack spacing={compact ? 0.5 : 0.75}>
					<Typography
						variant={compact ? "caption" : "subtitle2"}
						fontWeight={800}
						sx={{
							color: groupAccent(g.title),
							display: "inline-block",
						}}
					>
						{g.title}
					</Typography>
					<Typography
						variant="caption"
						sx={{ fontFamily: "monospace" }}
					>
						CAN: {g.can?.id || "—"} | Payload:{" "}
						{g.can?.payload_hex || "—"}
					</Typography>
					{(g.subgroups || []).map((sg, idx) => (
						<Fragment key={`${sg.title}-${idx}`}>
							<Divider />
							<Typography
								variant={compact ? "caption" : "body2"}
								fontWeight={700}
							>
								{sg.title}
							</Typography>
							{renderRowsTable(sg.rows || [])}
						</Fragment>
					))}
				</Stack>
			</Paper>
		);
	};

	return (
		<Box sx={{ p: compact ? 0 : 1.25 }}>
			<Stack
				direction={{ xs: "column", md: "row" }}
				alignItems={{ md: "center" }}
				justifyContent="space-between"
				spacing={compact ? 0 : 0.5}
				mb={compact ? 0.25 : 0.5}
			>
				<Typography variant="subtitle1">
					Live Data{" "}
					<Typography variant="caption">{chassisNum}</Typography>
				</Typography>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ lineHeight: compact ? 1.1 : 1.3 }}
				>
					Last update: {lastSeen || "—"}
				</Typography>
			</Stack>

			{/* CAN Data */}
			<Card variant="outlined" sx={{ mb: 1 }}>
				<CardContent sx={{ p: { xs: 0, sm: 1.25 } }}>
					{/* keep some padding just for the title on xs */}
					<Box sx={{ px: { xs: 1, sm: 0 }, pt: { xs: 1, sm: 0 } }}>
						<Typography
							variant="subtitle2"
							fontWeight={700}
							gutterBottom
						>
							CAN Data
						</Typography>
					</Box>

					{/* groups grid — no horizontal padding on xs */}
					<Box
						sx={{
							px: { xs: 0, sm: 0 },
							display: "grid",
							gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
							columnGap: { xs: 0, md: 1 },
							rowGap: 1,
						}}
					>
						{(message?.can_data || []).map((g, idx) => (
							<Box key={`${g.title}-${idx}`}>
								{g.structure === "regular"
									? renderRegular(g)
									: g.structure === "merged"
									? renderMerged(g)
									: renderSplit(g)}
							</Box>
						))}
					</Box>
				</CardContent>
			</Card>
		</Box>
	);
}
